<template lang="pug">
v-container.px-sm-8.py-sm-4
  v-row.mt-sm-5(no-gutters)
    v-col(cols="12" sm="5")
      .d-flex.align-center
        v-text-field.rounded-lg.search-input(v-model="searchText" @input="queryChanged" :placeholder="$t('search_tasks')" hide-details dense solo flat height=40 background-color="neutral300")
          template(v-slot:append)
            eva-icon(name="search-outline" :fill="$colors.primary400")
        v-btn.ml-3(v-if="isMobileScreen" @click="createTask" outlined icon color="info")
          v-icon mdi-plus
    v-col(cols="12" sm="7" v-if="!isMobileScreen")
      .text-right
        v-btn.button-md(@click="createTask" color="info500" outlined)
          eva-icon.mr-2.ml-n1(name="plus-outline" :fill="$colors.info" width=20 height=20 style="margin-top: 3px")
          span {{ $t('new_task') }}

  .mt-7.d-flex.align-center.justify-end(v-if="!isMobileScreen")
    yc-pagination-custom.ml-4(v-model="page" :parentPageSize.sync="pageSize" :count="totalCount" :text="$t('show_tasks')" @input="pageChanged")
  .mt-4(v-else)
    template(v-if="!isLoading")
      v-divider
      .d-flex.align-center.py-3
        v-spacer
        yc-pagination(v-model="page" :pageSize="pageSize" :count="totalCount")
      v-divider
  .text-center(v-if="isLoading")
    v-data-table.mt-3(v-if="!isMobileScreen" :headers="headers" hide-default-footer disable-sort)
      template(v-slot:no-data)
        div
    v-progress-circular.mt-4(width="2" size="50" color="primary" indeterminate)
  
  template(v-else)
    template(v-if="isMobileScreen")
      .pb-16(v-if="tasks.length")
        div(v-for="(task, index) in tasks" :key="task.id")
          .text-sm.py-2
            a.text-sm.font-medium.info500--text(@click="openTask(task)") {{ task.title }}
            .d-flex.mb-1.mt-2
              .flex-grow-1.basic600--text {{ $t('created_on') }}
              div {{ task.created | displayDate }}
            .d-flex.mb-1
              .flex-grow-1.basic600--text {{ $t('due_by') }}
              div {{ task.due_date | displayDate }}
            .d-flex.mb-1
              .flex-grow-1.basic600--text {{ $t('assignees') }}
              div {{ task.assign_to.length }}
            .d-flex.mb-1
              .flex-grow-1.basic600--text {{ $t('customers') }}
              div {{ task.total_customer }}
            .d-flex.mb-1
              .flex-grow-1.basic600--text {{ $t('segments') }}
              div(v-if="task.group")
                template(v-for="group in task.group")
                  y-chip.ma-1(:label="group.title" outlined)
            .d-flex.mb-1
              .flex-grow-1.basic600--text {{ $t('added_by') }}
              div {{ task.member | getMemberName }}
            .d-flex.mb-1
              .flex-grow-1.basic600--text {{ $t('engagement_rate') }}
              div {{ getPercentage(task) }}%
          v-divider(v-if="tasks.length - 1 != index")
      
      .mt-16.text-center(v-else)
        v-img.mx-auto(src="@/assets/images/icon_empty.png" width=80)
        .mt-2.p2.basic600--text {{ $t('no_tasks_available') }}
      .yc-fixed-bottom-section
        yc-pagination-custom(v-model="page" :parentPageSize.sync="pageSize" :count="totalCount" :text="$t('show_tasks')" @input="pageChanged")
    
    .yc-table(v-else)
      v-data-table.mt-4(:headers="headers" :items="tasks" :items-per-page="pageSize" :hide-default-header="isMobileScreen" hide-default-footer disable-sort)
        template(v-slot:no-data)
          .mt-16
            v-img.mx-auto(src="@/assets/images/icon_empty.png" width=80)
            .mt-2.p2.basic600--text {{ $t('no_tasks_available') }}
        
        template(v-slot:item="{ item, isMobile, headers, index }")
          tr.primary--text(v-if="!isMobile")
            td(v-for="header in headers")
              template(v-if="header.value === 'title'")
                a.text-sm.font-medium.info500--text(@click="openTask(item)") {{ item.title }}
              template(v-if="header.value === 'assignee'")
                div {{ item.assign_to.length }}
              template(v-if="header.value === 'customers'")
                div {{ item.total_customer }}
              template(v-if="header.value === 'group'")
                div(v-if="item.group")
                template(v-for="group in item.group")
                  y-chip.ma-1(:label="group.title" outlined)
              template(v-if="header.value === 'date'")
                .py-3
                  div Created {{ item.created | displayDate }}
                  div(v-if="item.due_date") Due {{ item.due_date | displayDate }}
              template(v-if="header.value === 'member'")
                div {{ item.member | getMemberName }}
              template(v-if="header.value === 'rate'")
                div {{ getPercentage(item) }}%
  create-task-dialog(v-if="showCreateDialog" :open.sync="showCreateDialog" @added="reload")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import CreateTaskDialog from '@/components/connect/dialogs/CreateTaskDialog.vue'

export default {
  components: { CreateTaskDialog },
  data: () => ({
    isLoading: true,
    searchText: '',
    page: 1,
    pageSize: 20,
    totalCount: 0,
    tasks: [],
    showCreateDialog: false,
    headers: [
      { text: 'Task', value: 'title' },
      { text: 'Assignee', value: 'assignee' },
      { text: 'Customers', value: 'customers' },
      { text: 'Segments', value: 'group' },
      { text: 'Date', value: 'date' },
      { text: 'Added by', value: 'member' },
      { text: 'Engagement Rate', value: 'rate' }
    ],
  }),
  watch: {
    pageSize() {
      this.fetchTasks(1)
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    isMobileScreen() {
      return this.$vuetify.breakpoint.xs
    }
  },
  created() {
    this.headers.map(item => item['class'] = 'text-table-header')
    this.fetchTasks(1)
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchTasks(page, query) {
      this.isLoading = true
      const params = {
        page_size: this.pageSize,
        page,
        search: query || '',
        expand: 'member,group'
      }
      this.$axios.get(`/connect/${this.company.slug}/tasks?ordering=-id`, { params })
      .then(resp => {
        const data = resp.data
        this.tasks = data.results
        this.totalCount = data.count
      })
      .catch(() => {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      })
      .finally(() => {
        this.isLoading = false
      })
    },
    queryChanged: debounce(function (query) {
      this.fetchTasks(1, query)
    }, 750),
    pageChanged(num) {
      this.fetchTasks(num)
    },
    openTask(task) {
      this.$router.push(`/admin/connect/tasks/${task.id}`)
    },
    createTask() {
      this.showCreateDialog = true
    },
    reload() {
      this.fetchTasks()
    },
    getPercentage(task) {
      const percentage = (task.total_engaged_customer / task.total_customer) * 100 || 0
      return percentage.toFixed(2)
    }
  }
}
</script>
